import {useEffect, useRef, useState} from 'react';
import useTranslation from 'next-translate/useTranslation';

import Modal from '@/components/ui/Modal';
import useTracking from '@/hooks/useTracking';
import {useCommitEventBarFulfillment} from './hooks/useCommitEventBarFulfillment';
import {useMenuFulfillmentPending} from './hooks/useMenuFulfillmentPending';
import {formatWhenSummary} from './utils/when';
import {useEventBarV2} from './EventBarV2Provider';

type MenuUpdateConfirmationModalProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

// The menu-page "catch-all": when a user edits Where/When/Who but leaves the bar without clicking a
// panel's Update button.
const MenuUpdateConfirmationModal: React.FC<MenuUpdateConfirmationModalProps> = ({
  isOpen,
  onOpenChange,
}) => {
  const {t} = useTranslation('app-bar');
  const {track, trackClick} = useTracking();
  const {commitFulfillment} = useCommitEventBarFulfillment();
  const {
    whereValue,
    whereSelection,
    hasUserEditedWhereRef,
    fulfillmentType,
    whenDate,
    whenTime,
    headCount,
    eventName,
    resetToFulfillmentDetail,
    resetWhereAddressToFulfillmentDetail,
  } = useEventBarV2();
  const pending = useMenuFulfillmentPending();

  const [isApplying, setIsApplying] = useState(false);
  // Set when the discard button drives the close, so the dismiss (X / Escape / overlay) handler
  // below doesn't also fire the close event for the same interaction.
  const isClosingViaDiscardRef = useRef(false);

  const whenSummary = formatWhenSummary({whenDate, whenTime});

  useEffect(() => {
    if (!isOpen) return;
    track('event-bar-v2-apply-details-confirmation-modal-open', {
      page: window.location.pathname,
      sub_category: 'navigation bar',
    });
  }, [isOpen, track]);

  const handleApply = async () => {
    trackClick('event-bar-v2-apply-details-confirmation-modal-apply-button');
    setIsApplying(true);
    // A typed-but-unresolved Where address isn't committed (only a valid selection is), so restore
    // the previously committed address rather than leaving the invalid text in the bar after Apply.
    if (hasUserEditedWhereRef.current && !whereSelection) {
      resetWhereAddressToFulfillmentDetail();
    }
    // Keep the modal open on a failed commit so the pending edit isn't silently lost.
    const {ok} = await commitFulfillment();
    setIsApplying(false);
    if (ok) onOpenChange(false);
  };

  const handleDiscard = () => {
    isClosingViaDiscardRef.current = true;
    trackClick('event-bar-v2-apply-details-confirmation-modal-discard-button');
    resetToFulfillmentDetail();
  };

  // Closing via the X, Escape, or a click outside is treated as a discard: revert the staged edit
  // and fire the close event, unless Discard drove the close.
  const handleModalOpenChange = (open: boolean) => {
    if (!open && !isClosingViaDiscardRef.current) {
      trackClick('event-bar-v2-apply-details-confirmation-modal-close-button');
      resetToFulfillmentDetail();
    }
    if (!open) isClosingViaDiscardRef.current = false;
    onOpenChange(open);
  };

  return (
    <Modal
      cancelAction={{
        label: t('eventBarV2.applyDetailsConfirmation.button.discard'),
        onClick: handleDiscard,
      }}
      data-testid="event-bar-v2-apply-details-confirmation-modal"
      disablePointerDismissal
      // This modal is opened by leaving the bar, not by a trigger inside it, so there is nothing to
      // hand focus back to. Base UI's default would restore the field input that was focused before
      // — and the fields open their panel on focus, so applying would immediately reopen the panel
      // the user had just finished with. Leave focus where it is instead.
      finalFocus={false}
      onOpenChange={handleModalOpenChange}
      open={isOpen}
      primaryAction={{
        closeOnComplete: false,
        label: t('eventBarV2.applyDetailsConfirmation.button.apply'),
        loading: isApplying,
        onClick: handleApply,
      }}
      title={t('eventBarV2.applyDetailsConfirmation.title')}
    >
      <div className="grid grid-cols-[auto_1fr] gap-2">
        {pending.strategyPending && (
          <>
            <span className="font-bold">{t('eventBarV2.where.fulfillment.label')}:</span>
            <span>{t(`eventBarV2.where.fulfillment.${fulfillmentType}`)}</span>
          </>
        )}
        {pending.addressPending && (
          <>
            <span className="font-bold">{t('eventBarV2.applyDetailsConfirmation.address')}:</span>
            <span>{whereValue}</span>
          </>
        )}
        {pending.dateTimePending && (
          <>
            <span className="font-bold">{t('eventBarV2.applyDetailsConfirmation.dateTime')}:</span>
            <span>{whenSummary || t('eventBarV2.when.anytime')}</span>
          </>
        )}
        {pending.headcountPending && (
          <>
            <span className="font-bold">{t('eventBarV2.applyDetailsConfirmation.people')}:</span>
            <span>
              {Number.isFinite(headCount)
                ? headCount
                : t('eventBarV2.applyDetailsConfirmation.none')}
            </span>
          </>
        )}
        {pending.eventNamePending && (
          <>
            <span className="font-bold">{t('eventBarV2.applyDetailsConfirmation.eventName')}:</span>
            <span>{eventName}</span>
          </>
        )}
      </div>
    </Modal>
  );
};

export default MenuUpdateConfirmationModal;
